import { GetStaticPaths, GetStaticProps } from "next";
import { FunctionComponent, useState } from "react";
import {
  Collection,
  ShopPageNavSwitcher,
  ShopPageNavProps,
  ShopPageNav,
  ShopPageNavItem,
  Hero,
  HeroProps,
} from "@bluebottlecoffee/design-system";
import { QueryParam } from "@bluebottlecoffee/design-system/dist/lib/types/router";
import { useAnalytics } from "../../../../lib/chord/packages/web/src/hooks/actions/use-analytics";
import { filters as f, queries } from "../../../../lib/sanity/shared";
import sanityClient from "../../../../lib/sanity-client";
import {
  Product as ProductSchema,
  Config as ConfigSchema,
} from "../../../../lib/sanity-schema";
import {
  assignOnClick,
  ShopCardPropsWithSlug,
  toShopCardProps,
} from "../../../../lib/transformers/shop-card";
import { AlgoliaShopCollectionProduct } from "../../../../lib/algolia/types";
import {
  buildSanityQuery,
  SanityQueryType,
  ShopCollectionNavQueryResult,
} from "../../../../lib/sanity/collection-queries";
import { toShopPageNavItems } from "../../../../lib/transformers/shop-page-nav";
import {
  debugPrintBuilder,
  getDefaultStaticPropsWithSlug,
  getIsClientSide,
  getPrintableQueryParam,
} from "../../../../lib/utils";
import { shopPage } from "../../../../lib/link-builders";
import { shopCollectionProductsIndex } from "../../../../lib/algolia/indices";
import { LayoutWrapper } from "../../../../components/LayoutWrapper";
import { toHeroProps } from "../../../../lib/transformers/hero";

interface PageData extends SanityQueryType {
  shopCards: ShopCardPropsWithSlug[];
}

export const getStaticProps: GetStaticProps<PageData> = async (context) => {
  const { props: defaultProps } = await getDefaultStaticPropsWithSlug(context);

  const { lang, region, slug } = defaultProps;

  const sanityQuery = buildSanityQuery(slug, lang);

  const result = await Promise.all([
    sanityClient().fetch<SanityQueryType>(sanityQuery),
    shopCollectionProductsIndex.search<AlgoliaShopCollectionProduct>("", {
      facetFilters: [[`shopCollectionSlug:${slug}`], ["forSale:true"]],
    }),
  ]);
  const sanityResult: SanityQueryType = result[0];
  const algoliaProducts: AlgoliaShopCollectionProduct[] = result[1].hits;
  const shopCards: ShopCardPropsWithSlug[] = algoliaProducts.map(
    (algoliaProduct) => toShopCardProps(algoliaProduct, lang, region),
  );

  return {
    props: {
      ...defaultProps,
      ...sanityResult,
      shopCards,
    },
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const config = await sanityClient().fetch<ConfigSchema>(queries.configQuery);

  const collections = await sanityClient().fetch<ProductSchema[]>(`*[
    ${f.whereType("shopCollection")} &&
    ${f.excludeDrafts}
  ]`);

  const params = config.languages.flatMap((lang) =>
    collections.map((collection) => ({
      params: {
        region: config.region,
        lang,
        slug: collection.slug.current,
      },
    })),
  );

  return {
    paths: params,
    fallback: false,
  };
};

function addTrackingToShopCards(
  shopCards: ShopCardPropsWithSlug[],
  trackClickProduct: (slug: string) => any,
) {
  return shopCards.map((shopCard) =>
    assignOnClick(shopCard, () => trackClickProduct(shopCard.productSlug)),
  );
}

const ShopCollectionPage: FunctionComponent<PageData> = ({
  navLinks,
  shopCollection,
  shopCards,
  ...layoutWrapperProps
}) => {
  const { region, lang, slug } = layoutWrapperProps;
  const debugPrint = debugPrintBuilder(`Collection/${slug}`);
  const { trackClickCollection, trackClickProduct } = useAnalytics();

  // State Block
  const defaultShopPageNavItems = toShopPageNavItems({
    region,
    lang,
    navLinks,
    trackClickCollection,
  });
  const [shopPageNavItems, setShopPageNavItems] = useState<
    Pick<ShopPageNavProps, "navItems">[]
  >(defaultShopPageNavItems);
  // END State Block

  // Use Effect Block
  const handleNavQueryNotExists = (): void => {
    debugPrint(
      "Nav query does not exist or is not in the valid format of <slug>,<display value>",
    );
    setShopPageNavItems(defaultShopPageNavItems);
  };

  /**
   * Handle any business logic related to having a `nav` query in the URL.
   *
   * ## Requirements
   * - Decorate the matching link with the provided display value
   */
  const handleNavQueryExists = ({ link, display }: ShopPageNavItem): void => {
    function initShopPageNavItems() {
      /**
       * Use the display value from the nav query as the name on the link
       * instead of the one listed in the CMS as its name;
       */
      const decoratedNavLinks = navLinks.map((navLink) => {
        const isMatch = navLink.slug === link;
        debugPrint(
          `handleNavQueryExists: comparing ${navLink.slug} to ${link}`,
        );

        if (isMatch) debugPrint(`handleNavQueryExists: Match found ${display}`);
        const overiddenNavLink: ShopCollectionNavQueryResult = {
          slug: navLink.slug,
          shopCollections: navLink.shopCollections,
          name: display,
        };

        return isMatch ? overiddenNavLink : navLink;
      });

      const decoratedShopPageNavItems = toShopPageNavItems({
        region,
        lang,
        navLinks: decoratedNavLinks,
        trackClickCollection,
      });

      setShopPageNavItems(decoratedShopPageNavItems);
    }

    initShopPageNavItems();
  };
  // END Use Effect Block

  // Component Props Block
  const handleGetFiltersFromRoute = (filters: QueryParam): void => {
    debugPrint(
      filters.length
        ? `Getting filters from route: ${getPrintableQueryParam(filters)}`
        : "No filters in route",
    );
  };

  const handleAppendQueryFiltersToRoute = (filters: QueryParam): void => {
    debugPrint(
      filters.length
        ? `appending filters to route: ${getPrintableQueryParam(filters)}`
        : "No Filters to append to route",
    );
  };

  const handleClearFilters = (filters: QueryParam): void => {
    debugPrint(`clearing filters ${getPrintableQueryParam(filters)}`);
  };

  const shopPageNavProps: ShopPageNavProps[] = shopPageNavItems.map(
    (navItem) => ({
      ...navItem,
      onAppendQueryFiltersToRoute: handleAppendQueryFiltersToRoute,
      onClearFilters: handleClearFilters,
      onGetFiltersfromRoute: handleGetFiltersFromRoute,
      debugPrint: debugPrintBuilder("ShopPageNav"),
    }),
  );
  // END Component Props Block

  let heroProps: HeroProps;
  try {
    heroProps =
      shopCollection.hero && toHeroProps(shopCollection.hero, region, lang);
  } catch (e) {
    const error = `The ${shopCollection.name} collection is failing with message:\n${e.message} `;
    console.error(error);
    throw new Error(error);
  }

  return (
    <LayoutWrapper
      {...{
        ...layoutWrapperProps,
        metaTags: shopCollection.metaTags,
      }}
    >
      {shopCollection.hero && <Hero {...heroProps} />}
      {getIsClientSide() && (
        <ShopPageNavSwitcher
          linkBuilder={{ shopPage }}
          onNavQueryExists={handleNavQueryExists}
          onNavQueryNotExists={handleNavQueryNotExists}
          debugPrint={debugPrintBuilder("ShopPageNavSwitcher")}
        >
          {shopPageNavProps.map((props) => (
            <ShopPageNav {...props} />
          ))}
        </ShopPageNavSwitcher>
      )}
      {shopCards.length > 0 && (
        <Collection
          name={shopCollection.name}
          shopCards={addTrackingToShopCards(shopCards, trackClickProduct)}
        />
      )}
    </LayoutWrapper>
  );
};

export default ShopCollectionPage;
